import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { platformBillingApi } from "@/lib/api/platformBilling";
import { workspaceAccessApi } from "@/lib/api/workspaceAccess";
import { platformAdminsApi } from "@/lib/api/platformAdmins";
import { qk } from "@/lib/query-client";
import { useAuthStore } from "@/stores/auth-store";
import { PageHeader } from "@/components/layout/PageHeader";
import { Icon } from "@/components/layout/Icon";
import { StatTile } from "@/components/ui/StatTile";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { DataTable } from "@/components/ui/Table";
import { formatDateTime, formatNumber } from "@/lib/utils/format";
import type { WorkspaceAccessSession } from "@/types/domain";

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * "Dashboard" - a Super Admin's own landing page (see HomeRoute), platform
 * KPIs only. Distinct from "Workspaces" (the tenant CRUD list):
 * this is a summary view, built entirely from data the platform surface
 * already exposes (platformBillingApi.overview spans every tenant
 * regardless of pagination) rather than a new backend endpoint - no
 * employee/attendance/payroll data is reachable from here, or anywhere in
 * this file.
 */
export function PlatformDashboardPage() {
  const user = useAuthStore((s) => s.user);

  const { data: tenants, isLoading: tenantsLoading } = useQuery({
    queryKey: qk.platformBilling.overview,
    queryFn: platformBillingApi.overview,
  });

  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: qk.workspaceAccess.list({ per_page: 5 }),
    queryFn: () => workspaceAccessApi.list({ per_page: 5 }),
  });

  const { data: admins } = useQuery({
    queryKey: qk.platformAdmins.list({ per_page: 100 }),
    queryFn: () => platformAdminsApi.list({ per_page: 100 }),
  });

  const tenantNameById = useMemo(() => new Map((tenants ?? []).map((t) => [t.tenant_id, t.tenant_name])), [tenants]);
  const adminNameById = useMemo(
    () => new Map((admins?.items ?? []).map((a) => [a.id, `${a.first_name} ${a.last_name}`.trim() || a.email])),
    [admins],
  );

  const stats = useMemo(() => {
    const list = tenants ?? [];
    return {
      total: list.length,
      active: list.filter((t) => t.tenant_status === "active").length,
      suspended: list.filter((t) => t.tenant_status === "suspended").length,
      trialing: list.filter((t) => t.subscription_status === "trialing").length,
    };
  }, [tenants]);

  return (
    <div>
      <PageHeader
        title={`Welcome back, ${user?.first_name ?? "Super Admin"}`}
        description="Platform-wide overview. No tenant employee, attendance or payroll data is ever shown here - open a workspace from Workspaces for tenant-scoped administration."
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="Workspaces"
          value={tenantsLoading ? "—" : formatNumber(stats.total)}
          hint="total provisioned"
          icon={<Icon name="layers" className="h-5 w-5" />}
        />
        <StatTile
          label="Active"
          value={tenantsLoading ? "—" : formatNumber(stats.active)}
          hint="workspaces active"
          icon={<Icon name="check-circle" className="h-5 w-5" />}
        />
        <StatTile
          label="Suspended"
          value={tenantsLoading ? "—" : formatNumber(stats.suspended)}
          hint="workspaces suspended"
          icon={<Icon name="x-circle" className="h-5 w-5" />}
        />
        <StatTile
          label="On Trial"
          value={tenantsLoading ? "—" : formatNumber(stats.trialing)}
          hint="subscriptions trialing"
          icon={<Icon name="clock" className="h-5 w-5" />}
        />
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Recent Workspace Access</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <DataTable<WorkspaceAccessSession>
            loading={sessionsLoading}
            data={sessions?.items ?? []}
            keyField={(s) => s.id}
            emptyTitle="No workspace access recorded yet"
            emptyDescription="Every time a Super Admin opens a workspace, it's recorded here with who, when, why, and for how long."
            columns={[
              { key: "tenant", header: "Workspace", render: (s) => tenantNameById.get(s.tenant_id) ?? s.tenant_id.slice(0, 8) },
              { key: "admin", header: "Super Admin", render: (s) => adminNameById.get(s.super_admin_id) ?? s.super_admin_id.slice(0, 8) },
              { key: "reason", header: "Reason", render: (s) => <span className="text-slate-600">{s.reason}</span> },
              { key: "started", header: "Accessed", render: (s) => formatDateTime(s.started_at) },
              {
                key: "duration",
                header: "Duration",
                render: (s) =>
                  s.ended_at
                    ? formatDuration(Math.round((new Date(s.ended_at).getTime() - new Date(s.started_at).getTime()) / 1000))
                    : <span className="text-amber-600">Open</span>,
              },
            ]}
          />
        </CardBody>
      </Card>
    </div>
  );
}
